import { Action } from '../../actions';
import UtilsApi from '../../utils/api';
import { Dispatch } from 'react-redux';
import { StoreState } from '../../reducers';
import { initializeGallery } from '../Gallery/actions';
import { createNotification } from '../NotificationBar/actions';
import { AuthenticationUserState, Security, ConnectionStatus, AuthenticationMode } from './reducer';
import RoutesUtils from '../../utils/routes';
import LocalStorageManager from '../../utils/localStorage/manager';
import UserAgentUtils from '../../utils/userAgent';
import AuthUtils from '../../utils/authentication';

export enum AuthenticationActionKeys {
	SET_FORM_LOGIN = '@@authentication/SET_FORM_LOGIN',
	SET_FORM_PASSWORD = '@@authentication/SET_FORM_PASSWORD',
	REQUEST_AUTHENTICATION = '@@authentication/REQUEST_AUTHENTICATION',
	UPDATE_CREDENTIALS = '@@authentication/UPDATE_CREDENTIALS',
}

export interface SetFormLoginAction extends Action {
	type: AuthenticationActionKeys.SET_FORM_LOGIN;
	value: string;
}

export interface SetFormPasswordAction extends Action {
	type: AuthenticationActionKeys.SET_FORM_PASSWORD;
	value: string;
}

export interface RequestAuthenticationAction extends Action {
	type: AuthenticationActionKeys.REQUEST_AUTHENTICATION;
}

export interface UpdateCredentialsArgs {
	needCredentials?: boolean;
	login?: string;
	password?: string;
	mode?: AuthenticationMode;
}

export interface UpdateCredentialsAction extends Action {
	type: AuthenticationActionKeys.UPDATE_CREDENTIALS;
	userInfos: AuthenticationUserState;
}

export const loadPreviousCredentials = () => (dispatch: Dispatch<StoreState>): void => {
	const neededAuthenticationUserInfos = LocalStorageManager.getCredentials();

	if (neededAuthenticationUserInfos && neededAuthenticationUserInfos.mode) {
		// For Saml on iOS, the app needs to redo the authentication process automatically, so the connection status is changed to 'need_reconnect' if it was connected before
		const mode = AuthUtils.isSaml(neededAuthenticationUserInfos.mode) && UserAgentUtils.isIOSApp()
			? { ...neededAuthenticationUserInfos.mode,
				status: AuthUtils.isNotConnected(neededAuthenticationUserInfos.mode)
					? ConnectionStatus.NOT_CONNECTED
					: ConnectionStatus.NEED_RECONNECT
			}
			: neededAuthenticationUserInfos.mode;

		dispatch(updateCredentials({
			needCredentials: neededAuthenticationUserInfos.needCredentials,
			login: neededAuthenticationUserInfos.login,
			password: neededAuthenticationUserInfos.password,
			mode
		}));
	}
};

export const sendAuthentication = (login: string, password: string) => (dispatch: Dispatch<StoreState>): void => {
	dispatch({ type: AuthenticationActionKeys.REQUEST_AUTHENTICATION });

	UtilsApi.authenticate(login, password)
		.then(json => {
			dispatch(resetForm());
			dispatch(setCredentials(login, password));
		})
		.then(() => {
			dispatch(initializeGallery());
			RoutesUtils.historyPush(RoutesUtils.getPathDefault());
		})
		.catch(response => {
			if (response.status === 401) {
				dispatch(createNotification('error.login_or_password_incorrect'));
				return response;
			}
		});
};

export const disconnect = () => (dispatch: Dispatch<StoreState>): void => {
	dispatch(unsetCredentials());
	dispatch(redirectToAuthenticationPageIfNeeded());
};

export const setNeedCredentials = (value: boolean, security: Security, samlUrl: string) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const { form } = getState().authentication;

	if (form.login || form.password) {
		dispatch(resetForm());
	}

	dispatch(updateCredentials({
		needCredentials: value,
		login: '',
		password: '',
		mode: {
			method: security,
			status: ConnectionStatus.NOT_CONNECTED,
			onCache: security === Security.Excel ? undefined : false,
			samlUrl: security === Security.Excel ? undefined : samlUrl
		}
	}));

	dispatch(redirectToAuthenticationPageIfNeeded());
};

export const setCredentials = (login: string, password: string) => (dispatch: Dispatch<StoreState>): void => {
	dispatch(updateCredentials({
		login: login,
		password: password,
		mode: {
			method: Security.Excel,
			status: ConnectionStatus.CONNECTED
		}
	}));
};

export const unsetCredentials = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const { mode } = getState().authentication.userInfos;

	const resetMode = mode
		? {
			method: mode.method,
			status: ConnectionStatus.NOT_CONNECTED,
			samlUrl: mode.method === Security.Excel ? undefined : '',
			onCache: mode.method === Security.Excel ? undefined : false
		}
		: undefined;

	dispatch(updateCredentials({
		needCredentials: true,
		login: '',
		password: '',
		mode: resetMode
	}));
};

const resetForm = () => (dispatch: Dispatch<StoreState>): void => {
	dispatch({
		type: AuthenticationActionKeys.SET_FORM_LOGIN,
		value: '',
	});
	dispatch({
		type: AuthenticationActionKeys.SET_FORM_PASSWORD,
		value: '',
	});
};

function storeCredentials(userInfos: AuthenticationUserState): void {
	LocalStorageManager.setCredentials(userInfos);
}

export const updateCredentials = (updateCredentialsArgs: UpdateCredentialsArgs) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	let newCredentialsState = getState().authentication.userInfos;

	newCredentialsState.needCredentials = updateCredentialsArgs.needCredentials !== undefined
		? updateCredentialsArgs.needCredentials
		: newCredentialsState.needCredentials;
	newCredentialsState.login = updateCredentialsArgs.login !== undefined
		? updateCredentialsArgs.login
		: newCredentialsState.login;
	newCredentialsState.password = updateCredentialsArgs.password !== undefined
		? updateCredentialsArgs.password
		: newCredentialsState.password;
	newCredentialsState.mode = updateCredentialsArgs.mode !== undefined
		? updateCredentialsArgs.mode
		: newCredentialsState.mode;

	storeCredentials(newCredentialsState);

	dispatch({
		type: AuthenticationActionKeys.UPDATE_CREDENTIALS,
		userInfos: newCredentialsState,
	});
};

export const redirectToAuthenticationPageIfNeeded = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const { authentication } = getState();
	const { userInfos } = authentication;
	const { needCredentials, mode } = userInfos;

	if (needCredentials && AuthUtils.isNotConnected(mode)) {
		RoutesUtils.historyPush(RoutesUtils.getPathAuthentication());
	}
};

export type AuthenticationAction = SetFormLoginAction | SetFormPasswordAction | RequestAuthenticationAction
	| UpdateCredentialsAction;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Authentication/actions.ts